import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getYearOutcomes, setYearOutcome } from "../../../services/StatisticsService";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";
import DataTablePagination from "../common/DataTablePagination";

const DECISIONS = ["admitted", "admitted_with_credits", "repeating", "graduated", "excluded"];

// Saisie de la décision annuelle de chaque étudiant (admis, passant avec crédits,
// redoublant, sortant, exclu). Alimente les statistiques par année universitaire.
const YearOutcomesTable = ({ academicYearId, semesterId, departmentId, classId, extraParams = {}, onChanged }) => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canEdit = can(PERMISSIONS.STUDENT_PROFILE_UPDATE);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    if (!academicYearId) return;
    setLoading(true);
    getYearOutcomes(academicYearId, {
      search: search || undefined,
      semester_id: semesterId || undefined,
      department_id: departmentId || undefined,
      class_id: classId || undefined,
      ...extraParams,
      page,
      limit,
    })
      .then((res) => {
        setItems(res.data?.data?.items || []);
        setTotal(res.data?.data?.meta?.total || 0);
      })
      .catch(() => setError(t("pages.statistics.outcomesLoadError")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYearId, semesterId, departmentId, classId, JSON.stringify(extraParams), search, page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [academicYearId, semesterId, departmentId, classId, search, limit]);

  const save = async (row, decision, creditsToRetake) => {
    if (!decision) return;
    setSavingId(row.studentId);
    setError("");
    try {
      await setYearOutcome(academicYearId, row.studentId, {
        decision,
        credits_to_retake: decision === "admitted_with_credits" ? Number(creditsToRetake) || 0 : 0,
      });
      setItems((prev) => prev.map((r) => (r.studentId === row.studentId
        ? { ...r, decision, creditsToRetake: decision === "admitted_with_credits" ? Number(creditsToRetake) || 0 : 0 }
        : r)));
      if (onChanged) onChanged();
    } catch (err) {
      setError(err?.response?.data?.message || t("pages.statistics.outcomeSaveError"));
    } finally {
      setSavingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pagination = (variant) => (
    <DataTablePagination
      variant={variant}
      page={page}
      limit={limit}
      total={total}
      totalPages={totalPages}
      onPageChange={setPage}
      onLimitChange={setLimit}
    />
  );

  return (
    <div className="mt-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <h6 className="mb-0">{t("pages.statistics.yearDecisionsTitle")}</h6>
        <input
          type="search"
          className="form-control w-auto"
          placeholder={t("common.crud.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {error && <div className="alert alert-warning">{error}</div>}
      {pagination("header")}
      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th>{t("pages.statistics.matriculeColumn")}</th>
              <th>{t("pages.statistics.studentColumn")}</th>
              <th>{t("pages.statistics.averageColumn")}</th>
              <th>{t("pages.statistics.decisionColumn")}</th>
              <th>{t("pages.statistics.creditsToRetakeColumn")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="5" className="text-center py-3"><div className="spinner-border spinner-border-sm" role="status" /></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan="5" className="text-center py-3 text-muted">{t("pages.statistics.noDataAvailable")}</td></tr>
            )}
            {!loading && items.map((row) => (
              <tr key={row.studentId}>
                <td>{row.matricule || "-"}</td>
                <td>{row.name}</td>
                <td>{row.average != null ? `${row.average} / 20` : "-"}</td>
                <td>
                  <select
                    className="form-select form-select-sm"
                    value={row.decision || ""}
                    disabled={!canEdit || savingId === row.studentId}
                    onChange={(e) => save(row, e.target.value, row.creditsToRetake)}
                  >
                    <option value="">{t("pages.statistics.decisionNone")}</option>
                    {DECISIONS.map((d) => (
                      <option key={d} value={d}>{t(`pages.statistics.decision_${d}`)}</option>
                    ))}
                  </select>
                </td>
                <td style={{ maxWidth: 110 }}>
                  {row.decision === "admitted_with_credits" ? (
                    <input
                      type="number"
                      min="0"
                      className="form-control form-control-sm"
                      defaultValue={row.creditsToRetake}
                      disabled={!canEdit || savingId === row.studentId}
                      onBlur={(e) => {
                        if (Number(e.target.value) !== row.creditsToRetake) save(row, row.decision, e.target.value);
                      }}
                    />
                  ) : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination("footer")}
    </div>
  );
};

export default YearOutcomesTable;